import {models} from 'powerbi-client';

// [WARN] Do not shorten this import . It will create a circular dependency issue
import pdfLogo from './img/logo-pdf.png';
import powerPointLogo from './img/logo-ppt.png';
import {makeHumanFriendlyUrl} from './utils/data-formatting';

const BASE_URL = import.meta.env.VITE_PBIE_API_BASE_URL;

const API_ENDPOINTS = {
  secrets: {
    get: `${BASE_URL}/get-my-secrets`,
    update: `${BASE_URL}/update-secret`
  },
  payment: {
    getSubscriptions: `${BASE_URL}/get-customer-subscriptions`,
    toggleAdminMode: `${BASE_URL}/set-admin-mode`,
    create: `${BASE_URL}/create-checkout-session`,
    update: `${BASE_URL}/update-subscription`,
    bills: `${BASE_URL}/get-customer-invoices`,
    products: `${BASE_URL}/get-product-list`,
    authorizations: `${BASE_URL}/get-projects-authorization`,
    setPartnerCustomerId: `${BASE_URL}/set-partner-customer-id`
  },
  projects: {
    verifyConnection: `${BASE_URL}/verify-editor-connection`,
    initOauthLoginToConnector: `${BASE_URL}/get-oauth-state`,
    loginToConnector: `${BASE_URL}/create-editor-connection`,
    deleteEditorConnection: `${BASE_URL}/delete-editor-connection`,
    connectFolders: `${BASE_URL}/create-native-connection`,
    connectors: `${BASE_URL}/get-connected-editors`,
    create: {
      manual: `${BASE_URL}/create-project`
    },
    newConnectionDataIntegrationStatus: `${BASE_URL}/check-update-folder`,
    import: `${BASE_URL}/push-data-file`,
    findAll: `${BASE_URL}/get-my-projects`,
    update: `${BASE_URL}/update-project-parameters`,
    delete: `${BASE_URL}/delete-project`,
    addUser: `${BASE_URL}/invite-user`,
    deleteUser: `${BASE_URL}/delete-user-from-project`,
    contact: `${BASE_URL}/push-contact`,
    demo: `${BASE_URL}/set-demo`,
    refresh: {
      manual: `${BASE_URL}/run-manual-update`,
      enableDisableAuto: `${BASE_URL}/set-auto-update`
    }
  },
  workspaces: {
    adminExportData: `${BASE_URL}/export-platform-usage`,
    findAll: `${BASE_URL}/get-workspaces`,
    create: `${BASE_URL}/create-workspace`,
    delete: `${BASE_URL}/delete-workspace`,
    addUser: `${BASE_URL}/add-workspace-user`,
    deleteUser: `${BASE_URL}/delete-workspace-user`
  },
  users: {
    promoteOrDemote: `${BASE_URL}/upgrade-downgrade-owner`
  },
  profile: {
    me: `${BASE_URL}/me`,
    update: `${BASE_URL}/update-profile`,
    delete: `${BASE_URL}/delete-me`,
    getHelp: `${BASE_URL}/get-help`,
    onboarding: {
      get: `${BASE_URL}/get-user-onboarding`,
      save: `${BASE_URL}/save-onboarding-progression`
    }
  },
  consents: {
    get: `${BASE_URL}/get-consentments`,
    save: `${BASE_URL}/push-consentments`
  },
  miscellaneous: {
    maintenance: `${BASE_URL}/get-maintenance-status`
  },
  emmai: {
    getData: `${BASE_URL}/emmai-get-data`,
    createPractitioner: `${BASE_URL}/emmai-create-practitioner`,
    deletePractitioner: `${BASE_URL}/emmai-delete-practitioner`,
    updatePractitioner: `${BASE_URL}/emmai-update-practitioner`,
    addActivities: `${BASE_URL}/emmai-add-activities`
  },
  reports: {
    saveAs: `${BASE_URL}/push-report-save-as`,
    create: `${BASE_URL}/create-report`,
    import: `${BASE_URL}/import-report`,
    export: `${BASE_URL}/export-report`,
    exportReportToFile: `${BASE_URL}/export-report-to-file`,
    delete: `${BASE_URL}/delete-report`,
    getToken: `${BASE_URL}/get-embed-token`,
    deleteReportBudgetData: `${BASE_URL}/delete-budget`,
    createReportBudgetData: `${BASE_URL}/create-budget`,
    getSpreadsheetData: `${BASE_URL}/get-data-for-spreadsheet`,
    updateSpreadsheetData: `${BASE_URL}/push-data-from-spreadsheet`,
    getReportPageAudio: `${BASE_URL}/read-audio`,
    sendDocumentRequest: `${BASE_URL}/send-documents-request`,
    transcriptWorkingDay: `${BASE_URL}/transcript-working-day`,
    sendTranscription: `${BASE_URL}/send-transcription`,
    budget: {
      getBudgetsList: `${BASE_URL}/get-budgets-list`,
      export: `${BASE_URL}/export-budget-to-file`,
      import: `${BASE_URL}/import-budget-from-file`
    },
    views: {
      get: `${BASE_URL}/get-reports-states`,
      save: `${BASE_URL}/save-report-state`,
      delete: `${BASE_URL}/delete-report-state`
    },
    summary: {
      getData: `${BASE_URL}/get-summaries-data`,
      getModels: `${BASE_URL}/get-prompt-models`,
      saveModel: `${BASE_URL}/save-prompt`,
      deleteModel: `${BASE_URL}/delete-prompt-model`,
      update: `${BASE_URL}/update-summary`,
      generateSummary: 'https://drivn-functions-copilot.azurewebsites.net/generate-summary'
    },
    parameters: {
      sources: `${BASE_URL}/get-powerbi-connectors`,
      testGatewayConnection: `${BASE_URL}/test-dataset-datasource`,
      getGateways: `${BASE_URL}/get-dataset-datasource`,
      updateGatewayConnection: `${BASE_URL}/update-dataset-datasource`,
      updateParameters: `${BASE_URL}/update-dataset-parameters`,
      runDatasetRefresh: `${BASE_URL}/run-dataset-refresh`,
      scheduleUpdate: `${BASE_URL}/set-scheduled-refresh`,
      cancelDatasetRefresh: `${BASE_URL}/cancel-dataset-refresh`,
      checkDatasetRefresh: `${BASE_URL}/check-dataset-refresh`,
      getRefreshHistory: `${BASE_URL}/get-dataset-refreshes-history`
    }
  },
  chatbot: {
    getAssistantsAndThreads: `${BASE_URL}/get-copilot`,
    create: `${BASE_URL}/create-empty-thread`,
    delete: `${BASE_URL}/delete-thread`,
    // addMessage: `${BASE_URL}/copilot/add-message-to-thread`,
    addMessage: 'https://drivn-functions-copilot.azurewebsites.net/add-message-to-thread',
    getThreadMessages: `${BASE_URL}/get-thread-content`,
    sendFile: `${BASE_URL}/upload-file-for-copilot`
  },
  mappings: {
    get: `${BASE_URL}/get-custom-mapping`,
    reset: `${BASE_URL}/initialize-custom-mapping`,
    update: `${BASE_URL}/update-custom-mapping`
  }
};

const CONNECTORS = {
  manual: 'MANUAL',
  tiime: 'tiime',
  pennylane: 'pennylane',
  fulll: 'fulll'
};

const USER_ROLES = {
  ADMIN: 'admin',
  ADMIN_OFF: 'admin-off',
  PARTNER: 'partner',
  GUEST: 'guest',
  CUSTOMER: 'customer',
  TRIAL: 'trial',
  OFF_TRIAL: 'off-trial'
};

const MONTHS = [
  {
    name: 'Janvier',
    id: 1
  },
  {
    name: 'Février',
    id: 2
  },
  {
    name: 'Mars',
    id: 3
  },
  {
    name: 'Avril',
    id: 4
  },
  {
    name: 'Mai',
    id: 5
  },
  {
    name: 'Juin',
    id: 6
  },
  {
    name: 'Juillet',
    id: 7
  },
  {
    name: 'Août',
    id: 8
  },
  {
    name: 'Septembre',
    id: 9
  },
  {
    name: 'Octobre',
    id: 10
  },
  {
    name: 'Novembre',
    id: 11
  },
  {
    name: 'Décembre',
    id: 12
  }
];

const DEFAULT_PROJECTS_SCHEMAS = {
  gestion: 'fec',
  dsn: 'dsn',
  previ: 'previ',
  z: 'z'
};

const POWER_BI_REPORTS = [
  {
    name: 'DeFi Gestion',
    module: DEFAULT_PROJECTS_SCHEMAS.gestion
  },
  {
    name: 'DeFi Gestion [Test]',
    module: DEFAULT_PROJECTS_SCHEMAS.gestion
  },
  {
    name: 'DeFi Z [Dev]',
    module: DEFAULT_PROJECTS_SCHEMAS.z
  },
  {
    name: 'DeFi Z',
    module: DEFAULT_PROJECTS_SCHEMAS.z
  }
];

const POWER_BI_PROJECT_SLICER_NAME = 'Projet';

const POWER_BI_PAGES = {
  filters: {
    title: 'Filters',
    id: 'ReportSection4dceaab95e7ea0702b93'
  },
  reportHome: {
    title: "Mon chiffre d'affaires",
    id: 'ReportSection'
  },
  treasury: {
    title: 'Vérification cash',
    id: '5e807e310ac478782145'
  },
  reportCharges: {
    title: 'Mes charges',
    id: 'ReportSection10cab94bd5d4594e4bd4'
  },
  reportSynthesis: {
    title: 'Synthèse',
    id: 'ReportSection6bc42c10a4c88850de74'
  },
  clients: {
    title: 'Mes clients',
    id: 'ReportSectionbe780789d5b4e851ab0c'
  },
  defiPreviReportTreasury: {
    title: 'Home',
    id: 'ReportSection6d55f3357cabde321e79'
  },
  defiPreviReportHome: {
    title: "Prévisionnel à partir de l'historique",
    id: 'ReportSection5fa3ecdf8aa2e7835c76'
  },
  defiPreviReportBudget: {
    title: 'Prévisionnel à partir du budget',
    id: 'ReportSection973656b77606027b2cc3'
  },
  projectChoiceZ: {
    title: 'Project choice',
    id: 'ReportSectioncf4bd7dd0708a2b38363'
  }
};

const POWER_BI_REPORT_FILTERS = [
  {
    title: 'Projet',
    name: 'f8c4f35048580c801620',
    table: 'siren_parameters',
    column: 'project_name'
  }
];

const POWER_BI_SLICERS = {
  projectChoice: {
    id: 'a02fd76d1d984c13987d'
  },
  thresholdTreasury: {
    id: '021612936bddc0c01486'
  },
  thresholdImposition: {
    id: 'ba56f0665eaa4c403b9c'
  },
  chooseBudget: {
    id: 'b995e5789abc377dbb20'
  },
  chargesTabs: {
    id: 'e022bcdaf579a3acb2e1'
  }
};

const POWER_BI_VISUALS = {
  accountantExpectedElements: {
    name: 'cb0a8c72e408719262a3',
    title: 'Les éléments attendus par mon comptable (Compte 471)'
  },
  summary: {
    name: 'c1539748c777baa88c1f',
    title: 'HTML Content (lite)'
  }
};

const POWER_BI_REPORT_BOOKMARKS = {
  ecarts: {
    title: 'Ecarts',
    name: 'Bookmarke76a16f8bd9a56667ba5'
  },
  ratio: {
    title: 'Ratio CA',
    name: 'Bookmark08ddd257ab418678bcb9'
  },
  filters: {
    title: 'Filters',
    name: 'Bookmark062b11cc5cddd8250b50'
  },
  reportHome: {
    title: 'Home',
    name: 'Bookmarkfa38f228868b013063b0'
  },
  openOrCloseFilters: {
    title: 'pop filters',
    name: 'Bookmarke6d8df775752999070b7'
  },
  treasuryTabDefiPrevi: {
    title: 'tab trésorerie report Defi Previ',
    name: 'Bookmark15ee581709d2e5e415d1'
  },
  impositionTabDefiPrevi: {
    title: 'tab imposition report Defi Previ',
    name: 'Bookmarkec2909a7cb0692c1796e'
  },
  accountantExpectedElements: {
    title: 'Les éléments attendus par mon comptable',
    name: 'Bookmark52c4635248f0ce2a1953'
  },
  treasuryDefiGestion: {
    title: 'Init phase 2',
    name: 'Bookmarkfe36f26cc67ce6caed29'
  },
  projectChoiceDefiGestion: {
    title: 'Init phase 1',
    name: 'Bookmark01b9f80e4678744e4358'
  },
  soldes: {
    title: 'Mes solde de gestion',
    name: 'Bookmarkd142593f14af4af99047'
  },
  customMapping: {
    title: 'Plan comptable personnalisé',
    name: '8bb36709c3decb00675d'
  },
  defaultMapping: {
    title: 'Plan comptable général',
    name: '34816650b9d5d7ca90b0'
  }
};

const STRIPE_PORTAL_URLS = {
  dev: 'https://billing.stripe.com/p/login/test_aEUbLdaNXejFcEMeUU',
  prod: 'https://billing.stripe.com/p/login/dR66qAeYp84Y2UU4gg'
};

const PRODUCTS_BILLING_SCHEMES = {
  UNIT: 'per_unit',
  TIERED: 'tiered'
};

const PRODUCTS_BILLING_INTERVALS = {
  MONTH: 'month'
};

const REPORT_LABELS = {
  fec: 'DeFi Gestion',
  z: 'DeFi Z',
  previ: 'DeFi Prévi',
  notice: 'Notice'
};

const SNACKBAR_ACTIONS = {
  ACTUALIZE_DATASOURCES: 'actualizeDatasources',
  ACTUALIZE_DATASOURCES_SUCCESS: 'actualizeDatasourcesSuccess',
  SCHEDULE_REPORT_UPDATE_IN_PROGRESS: 'scheduleReportUpdateInProgress',
  SCHEDULE_REPORT_UPDATE_SUCCESS: 'scheduleReportUpdateSuccess',
  UPDATE_PARAMETERS_IN_PROGRESS: 'updateParametersInProgress',
  UPDATE_PARAMETERS_SUCCESS: 'updateParametersSuccess',
  UPDATE_SOURCE_IN_PROGRESS: 'updateSourceInProgress',
  UPDATE_REPORT_IN_PROGRESS: 'updateReportInProgress',
  UPDATE_REPORT_SUCCESS: 'updateReportSuccess',
  UPDATE_USER_SECRET: 'updateUserSecret',
  UPDATE_USER_SECRET_SUCCESS: 'updateUserSecretSuccess',
  DELETE_PRACTITIONER: 'deletePractitioner',
  DELETE_PRACTITIONER_SUCCESS: 'deletePractitionerSuccess',
  ADD_PRACTITIONER_ACTIVITIES: 'addPractitionerActivities',
  ADD_PRACTITIONER_ACTIVITIES_SUCCESS: 'addPractitionerActivitiesSuccess',
  UPDATE_PRACTITIONER: 'updatePractitioner',
  UPDATE_PRACTITIONER_SUCCESS: 'updatePractitionerSuccess',
  CREATE_PRACTITIONER: 'createPractitioner',
  CREATE_PRACTITIONER_SUCCESS: 'createPractitionerSuccess',
  CREATE_BUDGET_SUCCESS: 'createBudgetSuccess',
  ADD_FISCAL_YEAR_TO_BUDGET_SUCCESS: 'addFiscalYearToBudgetSuccess',
  CREATE_BUDGET_IN_PROGRESS: 'createBudgetInProgress',
  ADD_FISCAL_YEAR_TO_BUDGET: 'addFiscalYearToBudget',
  TOGGLE_ADMIN_MODE_IN_PROGRESS: 'toggleAdminModeInProgress',
  TOGGLE_ADMIN_MODE_SUCCESS: 'toggleAdminModeInSuccess',
  EXPORT_PLATFORM_USAGE_DATA_IN_PROGRESS: 'exportPlatformUsageDataInProgress',
  EXPORT_PLATFORM_USAGE_DATA_SUCCESS: 'exportPlatformUsageDataSuccess',
  EXPORT_BUDGET_FILE_IN_PROGRESS: 'exportBudgetFileInProgress',
  EXPORT_BUDGET_FILE_SUCCESS: 'exportBudgetFileSuccess',
  IMPORT_BUDGET_FILE_SUCCESS: 'importBudgetFileSuccess',
  IMPORT_BUDGET_FILE_IN_PROGRESS: 'importBudgetFileInProgress',
  GENERATE_ANALYSIS_IN_PROGRESS: 'generateAnalysisInProgress',
  DELETE_PROMPT_MODEL_IN_PROGRESS: 'deletePromptModelInProgress',
  DELETE_THREAD_IN_PROGRESS: 'deleteThreadInProgress',
  DELETE_THREAD_SUCCESS: 'deleteThreadError',
  ADD_USER_SUCCESS: 'addUserSuccess',
  DELETE_PROJECT_IN_PROGRESS: 'deleteProjectInProgress',
  DELETE_PROJECT_SUCCESS: 'deleteProjectSuccess',
  DELETE_USER_SUCCESS: 'deleteUserSuccess',
  TECHNICAL_ERROR: 'technicalError',
  SEND_CONTACT_MESSAGE_SUCCESS: 'sendContactMessageSuccess',
  DELETE_BUDGET_IN_PROGRESS: 'deleteBudgetInProgress',
  DELETE_BUDGET_SUCCESS: 'deleteBudgetSuccess',
  IMPORT_PROJECT_FILES_SUCCESS: 'importProjectFilesSuccess',
  IMPORT_PROJECT_FILES_IN_PROGRESS: 'importProjectFilesInProgress',
  IMPORT_PROJECT_FILES_ERROR: 'importProjectFilesError',
  DELETE_REPORT_IN_PROGRESS: 'deleteReportInProgress',
  CREATE_PROJECT_IN_PROGRESS: 'createProjectInProgress',
  CREATE_PROJECT_SUCCESS: 'createProjectSuccess',
  CREATE_PROJECT_SUCCESS_WITH_NAME: 'createProjectSuccessWithName',
  UPDATE_PROJECT_IN_PROGRESS: 'updateProjectInProgress',
  UPDATE_PROJECT_SUCCESS: 'updateProjectSuccess',
  REFRESH_PROJECT_LOADING: 'refreshProjectLoading',
  REFRESH_PROJECT_SUCCESS: 'refreshProjectSuccess',
  PROMOTE_USER_SUCCESS: 'promoteUserSuccess',
  DEMOTE_USER_SUCCESS: 'demoteUserSuccess',
  UPDATE_PROFILE_SUCCESS: 'updateProfileSuccess',
  UPDATE_PROFILE_ERROR: 'updateProfileError',
  UPDATE_PROFILE_PARTIAL: 'updateProfilePartial',
  SUBSCRIBE_LOADING: 'subscribeLoading',
  UPLOAD_ERROR_DOCUMENT_TYPE: 'uploadErrorDocumentType',
  UPLOAD_ERROR_TOO_MANY_FILES: 'uploadErrorTooManyFiles',
  CREATE_CONNECTION_DATA_INTEGRATION_IN_PROGRESS: 'createConnectionDataIntegrationInProgress',
  PENNYLANE_DATA_INTEGRATION_COMPLETED: 'pennylaneDataIntegrationCompleted',
  CREATE_CONNECTION_DATA_INTEGRATION_ERROR: 'createConnectionDataIntegrationError',
  CREATE_WORKSPACE_SUCCESS: 'createWorkspaceSuccess',
  CREATE_WORKSPACE_IN_PROGRESS: 'createWorkspaceInProgress',
  DELETE_WORKSPACE_IN_PROGRESS: 'deleteWorkspaceInProgress',
  DELETE_WORKSPACE_SUCCESS: 'deleteWorkspaceSuccess',
  CREATE_REPORT_IN_PROGRESS: 'createReportInProgress',
  CREATE_REPORT_SUCCESS: 'createReportSuccess',
  REPORT_SUCCESSFULLY_SAVED: 'reportSuccessfullySaved',
  SEND_CHATBOT_MESSAGE_ERROR: 'sendChatbotMessageError',
  SUBSCRIBE_ERROR_EMAIL_ALREADY_TAKEN: 'subscribeErrorEmailAlreadyTaken',
  UPDATE_SPREADSHEET_DATA_IN_PROGRESS: 'updateSpreadsheetDataInProgress',
  UPDATE_SPREADSHEET_DATA_SUCCESS: 'updateSpreadsheetDataSuccess',
  UPDATE_SPREADSHEET_DATA_ERROR: 'updateSpreadsheetDataError',
  IMPORT_REPORT_IN_PROGRESS: 'importReportInProgress',
  IMPORT_REPORT_SUCCESS: 'importReportSuccess',
  EXPORT_REPORT_IN_PROGRESS: 'exportReportInProgress',
  EXPORT_REPORT_SUCCESS: 'exportReportSuccess',
  UPDATE_REPORT_SUMMARY: 'updateReportSummary',
  UPDATE_REPORT_SUMMARY_WITH_AUDIO: 'updateReportSummaryWithAudio',
  SEND_DOCUMENT_REQUEST_SUCCESS: 'sendDocumentRequestSuccess',
  UPDATE_REPORT_SUMMARY_SUCCESS: 'updateReportSummarySuccess',
  USER_HAS_DECLINED_MICROPHONE_PERMISSION: 'userHasDeclinedMicrophonePermission',
  TRANSCRIPTION_AUDIO_FILE_EXCEEDS_MAXIMUM_SIZE: 'transcriptionAudioFileExceedsMaximumSize',
  TRANSCRIPTION_AUDIO_FILE_WILL_SOON_EXCEED_MAXIMUM_SIZE_WARNING: 'transcriptionAudioFileWillSoonExceedMaximumSizeWarning',
  INVALID_TRANSCRIPTION: 'invalidTranscription',
  CREATE_PROMPT_MODEL_SUCCESS: 'createPromptModelSuccess',
  DELETE_PROMPT_MODEL_SUCCESS: 'deletePromptModelSuccess',
  RESET_CUSTOM_MAPPING_IN_PROGRESS: 'resetCustomMappingInProgress',
  RESET_CUSTOM_MAPPING_SUCCESS: 'resetCustomMappingInSuccess',
  UPDATE_CUSTOM_MAPPING_IN_PROGRESS: 'updateCustomMappingInProgress',
  UPDATE_CUSTOM_MAPPING_SUCCESS: 'updateCustomMappingInSuccess',
  DELETE_EDITOR_CONNECTION: 'deleteEditorConnection',
  DELETE_EDITOR_CONNECTION_SUCCESS: 'deleteEditorConnectionSuccess',
  SAVE_REPORT_VIEW_IN_PROGRESS: 'saveReportViewInProgress',
  SAVE_REPORT_VIEW_SUCCESS: 'saveReportViewInSuccess',
  APPLY_VIEW_SUCCESS: 'applyViewSuccess',
  DELETE_REPORT_VIEW_IN_PROGRESS: 'deleteReportViewInProgress',
  DELETE_REPORT_VIEW_SUCCESS: 'deleteReportViewSuccess',
  RESET_REPORT_VIEW_SUCCESS: 'resetReportViewSuccess',
  CREATE_CONNECTION_DATA_INTEGRATION_SUCCESS: 'createConnectionDataIntegrationSuccess'
};

const createMappingObjectBetweenReportNameAndReportUrlId = reportName => ({
  reportName,
  urlFormattedReportName: makeHumanFriendlyUrl(reportName)
});

const REPORT_URL_FORMATTED_IDS = [
  createMappingObjectBetweenReportNameAndReportUrlId(REPORT_LABELS.fec),
  createMappingObjectBetweenReportNameAndReportUrlId(REPORT_LABELS.z),
  createMappingObjectBetweenReportNameAndReportUrlId(REPORT_LABELS.previ),
  createMappingObjectBetweenReportNameAndReportUrlId(REPORT_LABELS.notice)
];

const BREAKPOINTS = {
  xs: 'extraSmall',
  sm: 'small',
  md: 'medium',
  lg: 'large',
  xl: 'extraLarge'
};

const REGULAR_EXPRESSIONS = {
  name: /^([A-Za-zÀ-ÖØ-öø-ÿ]+(?:[-'\s][A-Za-zÀ-ÖØ-öø-ÿ]+)*)$/,
  label: /^[^'";/-]*$/,
  textWithRangeChars: (minNumbersOfChars, maxNumberOfChars) => new RegExp(`^.{${minNumbersOfChars},${maxNumberOfChars}}$`, 's'),
  labelWithMinimumChars: numberOfChars => new RegExp(`^[^'";/-]{${numberOfChars},}$`),
  labelWithRangeChars: (minNumbersOfChars, maxNumberOfChars) => new RegExp(`^[^'";/-]{${minNumbersOfChars},${maxNumberOfChars}}$`),
  compteNum: /^\w{6,50}$/,
  url: /^(http|https):\/\/(www\.)?[a-zA-Z0-9.-]+(:\d+)?(\/[^\s]*)?$/,
  positiveOrNegativeAmount: /^-?[0-9]*$/,
  fecFileName: /^\d{9}_(?:\d{4}(?:0[1-9]|1[0-2])(?:0[1-9]|[12][0-9]|3[01]))\.txt$/,
  siret: /^[0-9]{14}$/,
  siren: /^[0-9]{9}$/,
  phone: /^(?:01|02|03|04|05|06|07|09)\d{8}$/,
  email: /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/,
  demoSiren: /^10000000\d[_\d\w-]*$/
};

const REGULAR_EXPRESSIONS_ERROR_TEXTS = {
  mandatoryField: 'Champ obligatoire',
  textWithRangeChars: (minNumbersOfChars, maxNumberOfChars) => `Veuillez entrer un texte valide (Entre ${minNumbersOfChars} et ${maxNumberOfChars} caractères)`,
  labelWithMinimumChars: numberOfChars => `Veuillez entrer un label valide (${numberOfChars} caractères minimum, caractères interdits: ', " , ; , / , - )`,
  labelWithRangeChars: (minNumbersOfChars, maxNumberOfChars) =>
    `Veuillez entrer un label valide (Entre ${minNumbersOfChars} et ${maxNumberOfChars} caractères, caractères interdits: ', " , ; , / , - )`,
  label: `Veuillez entrer un label valide (caractères interdits: ', " , ; , / , - )`,
  compteNum: 'Veuillez entrer un numéro de compte valide',
  positiveOrNegativeAmount: 'Veuillez entrer un montant valide',
  url: 'Veuillez entrer une URL valide',
  email: 'Veuillez entrer un email valide'
};

const VAT_RATES = [0, 0.055, 0.1, 0.2];

const PROJECTS_VIEW_TYPES = {
  list: 'list',
  card: 'card'
};

const PROJECTS_GROUPING_TYPES = {
  folder: 'folder',
  connection: 'connection'
};

const LOGO_THEME_VARIANTS = {
  dark: 'dark',
  light: 'light'
};

const DEFAULT_WORKSPACE = {
  workspace_name: 'Base de données',
  workspace_id: 'Base de données',
  isSelected: true
};

const EMBED_REPORT_DEFAULT_SETTINGS = {
  visualRenderedEvents: true,
  layoutType: models.LayoutType.Master,
  hideErrors: true,
  panes: {
    filters: {
      expanded: false,
      visible: false
    },
    pageNavigation: {
      visible: false
    }
  }
};

const EMBED_EDITABLE_REPORT_SETTINGS = {
  useCustomSaveAsDialog: true,
  layoutType: models.LayoutType.Master,
  hideErrors: true,
  panes: {
    filters: {
      expanded: false,
      visible: true
    },
    fields: {
      expanded: false
    },
    visualizations: {
      expanded: false
    },
    pageNavigation: {
      visible: true
    }
  }
};

const CHATBOT_SIDEBAR_WIDTH = 550;
const MODULES_SIDEBAR_WIDTH = 80;

const SIDEBAR_SINGLE_CARD_MARGIN_BOTTOM = 4;
const SIDEBAR_SINGLE_CARD_SECURITY_THRESHOLD = 10;
const SIDEBAR_SINGLE_CARD_HEIGHT = 50 + SIDEBAR_SINGLE_CARD_MARGIN_BOTTOM;
const SIDEBAR_CONTACT_BUTTON_SIZE = 60;
const SIDEBAR_MORE_ICON_SIZE = 40;

const CONVERSATIONS_TIME_PERIODS_TRANSLATIONS = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  last7Days: 'Les 7 derniers jours',
  last30Days: 'Les 30 derniers jours',
  thisMonth: 'Ce mois-ci',
  thisYear: 'Cette année'
};

// const VISUALS_TYPES_USER_CAN_SHARE_WITH_COPILOT = ['pivotTable', 'clusteredColumnChart', 'clusteredBarChart', 'areaChart', 'pieChart', 'lineChart'];
const VISUALS_TYPES_USER_CAN_SHARE_WITH_COPILOT = ['pivotTable'];

const CHATBOT_USER_MESSAGE_TYPES = {
  text: 'text',
  accountingFile: 'accountingFile'
};

const CHATBOT_MESSAGES_ROLES = {
  assistant: 'assistant',
  user: 'user'
};

const CHATBOT_ASSISTANT_ANSWER_STATE = {
  thinking: 'thinking',
  answering: 'answering'
};

const MAXIMUM_NUMBER_OF_CHABOT_MESSAGES_IN_TRIAL = 10;
const MAXIMUM_NUMBER_OF_CHABOT_THREADS = 2;

const TIIME_AUTHORIZE_URL = 'https://auth0.tiime.fr/authorize';
const TIIME_CLIENT_ID = 'M7VXA43KRbiMxx13gN85W8CcwDfzA5j2';

const appBaseUrl = import.meta.env.VITE_DB_ENV === 'prod' ? 'https://app.drivn.fr' : 'https://dev.app.drivn.fr';
// const appBaseUrl = 'http://localhost:3000';

const tiimeOauthCallbackRoute = '/tiime-oauth-callback';
const TIIME_OAUTH_CALLBACK_REDIRECT_URI = `${appBaseUrl}${tiimeOauthCallbackRoute}`;

const CONNECTOR_LOGIN_PROCESS_TYPE_LOCALSTORAGE_KEY = 'loginToConnectorProcessType';

const PENNYLANE_AUTHORIZE_URL = 'https://app.pennylane.com/oauth/authorize';
const pennylaneOauthCallbackRoute = '/pennylane-oauth-callback';
const PENNYLANE_OAUTH_CALLBACK_REDIRECT_URI = `${appBaseUrl}${pennylaneOauthCallbackRoute}`;

const FULLL_PROD_BASE_URL = 'https://auth.fulll.io';
const FULLL_DEV_BASE_URL = 'https://auth-fulll.fulll.house';
const FULLL_BASE_URL = import.meta.env.VITE_DB_ENV === 'prod' ? FULLL_PROD_BASE_URL : FULLL_DEV_BASE_URL;

const FULLL_AUTHORIZE_URL = `${FULLL_BASE_URL}/oauth2/authorize`;
// const FULLL_AUTHORIZE_URL = `https://auth.fulll.io/oauth2/authorize`;
const fulllOauthCallbackRoute = '/fulll-oauth-callback';
const FULLL_OAUTH_CALLBACK_REDIRECT_URI = `${appBaseUrl}${fulllOauthCallbackRoute}`;

const SPREADSHEET_TARGETS = {
  BUDGET: 'budget',
  FORECAST: 'forecast'
};

const SPINNER_TYPES = {
  DEFAULT: 'default',
  RSE: 'rse'
};

const EMPLOYEE_TYPES = {
  PARTNER: 'partner'
};

const APP_ROUTES = {
  home: '/',
  login: '/login',
  subscribe: '/subscribe',
  secrets: '/gerer-mes-secrets',
  report: reportId => `/reports/${reportId}`,
  connections: '/mes-connexions',
  subscription: '/mon-abonnement',
  profile: '/mon-profil',
  bills: '/mes-factures',
  thanksForPurchase: '/merci',
  help: (documentGroup, documentId) => `/${documentGroup}/${documentId}`,
  restrictedAccess: '/restricted-access'
};

const USERS_AUTHORIZATION_LEVELS = {
  workspace: 'workspace',
  report: 'report'
};

const MAXIMUM_NUMBER_OF_WORKSPACES = 2;
const MAXIMUM_NUMBER_OF_CUSTOM_REPORTS = 30;

const TRANSCRIPT_AUDIO_FILE_SIZE_WARNING_MESSAGE = 20;
const MAXIMUM_TRANSCRIPT_AUDIO_FILE_SIZE = 25;

const REPORT_LOADING_STEPS = {
  REPORT_LOADING: 'reportLoading',
  PROJECT_BOOKMARK_APPLYING: 'projectBookmarkApplying'
};

const CONNECTORS_ON_CLICK_ACTIONS = {
  OPEN_CREATE_CONNECTION_FORM: 'form',
  OPEN_CONTACT_FORM: 'contact'
};

const FORM_FIELD_TYPES = {
  text: 'text',
  password: 'password',
  drop_zone: 'drop_zone',
  siren: 'siren'
};

const SORT_TYPES = {
  NAME: 'name',
  COMPANY_ID: 'companyId'
};

const REPORT_MODULES_IDS = {
  DENTAL_MONTHLY_RESTITUTION: 'dentalMonthlyRestitution',
  EDIT_REPORT: 'editReport',
  CREATE_REPORT: 'createReport',
  DELETE_REPORT: 'deleteReport',
  REFRESH_REPORT: 'refreshReport',
  CREATE_BUDGET: 'createBudget',
  EDIT_BUDGET: 'editBudget',
  DELETE_BUDGET: 'deleteBudget',
  CREATE_SOCIAL_BUDGET: 'createSocialBudget',
  EDIT_SOCIAL_BUDGET: 'editSocialBudget',
  DELETE_SOCIAL_BUDGET: 'deleteSocialBudget',
  CUSTOMIZE_MAPPINGS: 'customizeMappings',
  RESET_CUSTOMIZE_MAPPINGS: 'resetCustomizeMappings',
  UPDATE_REPORT_SUMMARY: 'updateReportSummary',
  TRANSCRIPT_WORKING_DAY: 'transcriptWorkingDay',
  POWERPOINT_EXPORT: 'powerpointExport',
  INVITE_USER: 'inviteUser',
  SHARE_DOCUMENT_WITH_ACCOUNTANT: 'shareDocumentWithAccountant',
  EXPORT_REPORT: 'exportReport',
  UPLOAD_DATA: 'uploadData',
  SELECT_VIEW: 'selectView',
  SAVE_VIEW: 'saveView',
  RESET_VIEW: 'resetView',
  IMPORT_BUDGET: 'importBudget',
  EXPORT_BUDGET: 'exportBudget'
};

const REPORT_MODULES_CATEGORIES = {
  ACCOUNTING_BUDGET: {
    id: 'accountingBudget',
    label: 'Budget financier',
    icon: 'Euro',
    requiresProjectOwnership: true,
    modules: [
      {
        name: 'Modifier un budget',
        id: REPORT_MODULES_IDS.EDIT_BUDGET,
        formId: 'fec_budget',
        requiresSirenSelected: true,
        category: 'crudActions'
      },
      {
        name: 'Créer un budget',
        id: REPORT_MODULES_IDS.CREATE_BUDGET,
        formId: 'fec_budget',
        requiresSirenSelected: true,
        category: 'crudActions'
      },
      {
        name: 'Supprimer un budget',
        id: REPORT_MODULES_IDS.DELETE_BUDGET,
        formId: 'fec_budget',
        requiresSirenSelected: true,
        category: 'crudActions'
      },
      {
        name: 'Importer un budget',
        id: REPORT_MODULES_IDS.IMPORT_BUDGET,
        formId: 'fec_budget',
        requiresSirenSelected: true,
        category: 'importExportActions'
      },
      {
        name: 'Exporter un budget',
        id: REPORT_MODULES_IDS.EXPORT_BUDGET,
        formId: 'fec_budget',
        requiresSirenSelected: true,
        category: 'importExportActions'
      }
    ]
  },
  DENTAL: {
    id: 'dental',
    label: 'Restitution mensuelle',
    icon: 'EditCalendarOutlined',
    requiresProjectOwnership: false,
    modules: [
      {
        name: 'Restitution mensuelle',
        id: REPORT_MODULES_IDS.DENTAL_MONTHLY_RESTITUTION,
        formId: 'dental_clinic',
        requiresSirenSelected: true
      }
    ]
  },
  // SOCIAL_BUDGET: {
  //   id: 'socialBudget',
  //   label: 'Budget social',
  //   modules: [
  //     {
  //       name: 'Modifier un budget',
  //       id: REPORT_MODULES_IDS.EDIT_SOCIAL_BUDGET,
  //       formId: 'have_dsn_budget',
  //       requiresSirenSelected: true
  //     },
  //     {
  //       name: 'Créer un budget',
  //       id: REPORT_MODULES_IDS.CREATE_SOCIAL_BUDGET,
  //       formId: 'have_dsn_budget',
  //       requiresSirenSelected: true
  //     },
  //     {
  //       name: 'Supprimer un budget',
  //       id: REPORT_MODULES_IDS.DELETE_SOCIAL_BUDGET,
  //       formId: 'have_dsn_budget',
  //       requiresSirenSelected: true
  //     }
  //   ]
  // },
  CUSTOM_MAPPING: {
    id: 'customMapping',
    label: 'Plan des comptes',
    icon: 'AccountBalance',
    requiresProjectOwnership: true,
    modules: [
      {
        name: 'Créer / modifier le plan des comptes',
        id: REPORT_MODULES_IDS.CUSTOMIZE_MAPPINGS,
        formId: 'custom_mapping',
        requiresSirenSelected: true
      },
      {
        name: 'Réinitialiser le plan des comptes',
        id: REPORT_MODULES_IDS.RESET_CUSTOMIZE_MAPPINGS,
        formId: 'custom_mapping',
        requiresSirenSelected: true
      }
    ]
  },
  MANAGE: {
    id: 'manage',
    label: 'Gestion interne',
    requiresProjectOwnership: true,
    modules: [
      {
        name: 'Envoyer une demande de documents',
        id: REPORT_MODULES_IDS.SHARE_DOCUMENT_WITH_ACCOUNTANT,
        formId: 'missing_document',
        requiresSirenSelected: true
      },
      {
        name: 'Dicter les temps passés',
        id: REPORT_MODULES_IDS.TRANSCRIPT_WORKING_DAY,
        formId: 'is_timesheet',
        requiresSirenSelected: true
      }
    ]
  },
  SHARE: {
    id: 'share',
    label: 'Partage',
    modules: [
      {
        name: 'Inviter un utilisateur',
        id: REPORT_MODULES_IDS.INVITE_USER,
        requiresSirenSelected: false
      },
      {
        name: 'Exporter sous Powerpoint',
        id: REPORT_MODULES_IDS.POWERPOINT_EXPORT,
        requiresSirenSelected: false
      }
    ]
  },
  ANALYSIS: {
    id: 'analysis',
    label: 'Commentaires',
    icon: 'ContentPasteSearch',
    requiresProjectOwnership: true,
    modules: [
      {
        name: 'Créer un commentaire',
        id: REPORT_MODULES_IDS.UPDATE_REPORT_SUMMARY,
        formId: 'summary',
        requiresSirenSelected: true
      }
    ]
  },
  REPORT: {
    id: 'report',
    label: 'Rapport',
    icon: 'Description',
    modules: [
      {
        name: 'Modifier le rapport',
        id: REPORT_MODULES_IDS.EDIT_REPORT,
        requiresSirenSelected: false,
        category: 'reportActions'
      },
      {
        name: 'Exporter le rapport',
        id: REPORT_MODULES_IDS.EXPORT_REPORT,
        requiresSirenSelected: false,
        category: 'reportActions'
      },
      {
        name: 'Créer un rapport',
        id: REPORT_MODULES_IDS.CREATE_REPORT,
        requiresSirenSelected: false,
        category: 'reportActions',
        disabledTooltip: 'Vous devez être propriétaire de votre espace de travail pour créer un nouveau rapport.'
      },
      {
        name: 'Actualiser les visuels',
        id: REPORT_MODULES_IDS.REFRESH_REPORT,
        requiresSirenSelected: false,
        category: 'reportActions'
      },
      {
        name: 'Supprimer le rapport',
        id: REPORT_MODULES_IDS.DELETE_REPORT,
        requiresSirenSelected: false,
        category: 'reportActions'
      },
      {
        name: 'Choisir une vue',
        id: REPORT_MODULES_IDS.SELECT_VIEW,
        category: 'viewsActions',
        // requiresSirenSelected: true,
        hasSubMenu: true
      },
      {
        name: 'Sauvegarder cette vue',
        id: REPORT_MODULES_IDS.SAVE_VIEW,
        category: 'viewsActions'
        // requiresSirenSelected: true
      },
      {
        name: 'Réinitialiser le rapport',
        id: REPORT_MODULES_IDS.RESET_VIEW,
        category: 'viewsActions'
        // requiresSirenSelected: true
      }
    ]
  },
  DATA: {
    id: 'data',
    label: 'Données',
    icon: 'SdStorage',
    requiresProjectOwnership: true,
    modules: [
      {
        name: 'Importer les données',
        id: REPORT_MODULES_IDS.UPLOAD_DATA,
        requiresSirenSelected: true
      }
    ]
  }
};

const NON_EXPORTABLE_VISUAL_TYPES = ['shape', 'actionButton', 'pageNavigator', 'textbox', 'image'];

const REPORT_EXPORTING_STEPS = {
  EXPORTING_VISUALS: 'exportingVisuals',
  GENERATING_FILE: 'generatingFile'
};

const POWER_BI_VISUALS_SELECTORS_NEEDED_FOR_EXPORT = [
  {objectName: 'dataLabels', propertyName: 'visible'},
  {objectName: 'categoryAxis', propertyName: 'visible'},
  {objectName: 'valueAxis', propertyName: 'visible'},
  {objectName: 'legend', propertyName: 'visible'},
  // {objectName: 'legend', propertyName: 'titleText'}, TODO : Not existing in docs
  {objectName: 'border', propertyName: 'visible'},
  {objectName: 'border', propertyName: 'color'},
  {objectName: 'border', propertyName: 'radius'},
  {objectName: 'background', propertyName: 'visible'},
  {objectName: 'background', propertyName: 'color'},
  {objectName: 'background', propertyName: 'transparency'}
];

const EXPORTED_REPORT_FILE_TYPES = {
  POWERPOINT_AS_IMAGE: 'pptx',
  POWERPOINT_NATIVE: 'pptx-native',
  PDF: 'pdf'
};

const REPORT_EXPORT_FEATURES = [
  {
    id: EXPORTED_REPORT_FILE_TYPES.PDF,
    logo: pdfLogo,
    categoryTitle: 'PDF',
    options: [
      {
        title: 'Export simplifié',
        description: 'Exporter toutes les pages',
        disabled: false
      },
      {
        title: 'Export avancé',
        description: 'Exporter les pages sélectionnées',
        disabled: true
      }
    ]
  },

  {
    id: EXPORTED_REPORT_FILE_TYPES.POWERPOINT_AS_IMAGE,
    logo: powerPointLogo,
    categoryTitle: "Powerpoint en tant qu'image",
    options: [
      {
        title: 'Export simplifié',
        description: 'Exporter toutes les pages',
        disabled: false
      },
      {
        title: 'Export avancé',
        description: 'Exporter les pages sélectionnées',
        disabled: true
      }
    ]
  },

  {
    id: EXPORTED_REPORT_FILE_TYPES.POWERPOINT_NATIVE,

    logo: powerPointLogo,
    categoryTitle: 'Powerpoint natif (visuels modifiables)',
    options: [
      {
        title: 'Export simplifié',
        description: 'Exporter tous les visuels',
        disabled: true
      },
      {
        title: 'Export avancé',
        description: 'Exporter les visuels sélectionnés',
        disabled: true
      }
    ]
  }
];

const defaultTitleFontFamily = 'SoehneBreitKraftig';
const getTitleFontFamily = () => {
  if (import.meta.env.VITE_HAS_CUSTOM_FONT_FAMILY_FOR_TITLE === 'true') {
    return import.meta.env.VITE_TITLE_FONT_FAMILY;
  }

  return defaultTitleFontFamily;
};

const FONT_FAMILIES = {
  TITLE: getTitleFontFamily(),
  BODY: 'InstrumentSans'
};

const DEFAULT_FILE_FORMAT_ACCEPTED = {
  'text/plain': ['.txt']
};

const SPREADSHEETS_IDS = {
  FORECAST: 'forecast',
  BUDGET: 'budget',
  DOCUMENT_REQUEST: 'documentRequest',
  CUSTOM_MAPPINGS: 'customMappings',
  SUMMARY_DATA: 'summaryData',
  TRANSCRIPT_WORKING_DAY: 'transcriptWorkingDay'
};

const ADD_USER_FORMS_IDS = {
  CONNECTION: 'connection',
  WORKSPACE: 'workspace',
  REPORT: 'report'
};

const ROUNDING_MODES = {
  DOWN: 'down',
  UP: 'up'
};

const EXPORT_FORMATS = {
  PDF: 'pdf',
  XLSX: 'xlsx'
};

const DRIVN_CONTACT_EMAIL = 'contact@drivn.fr';

const INVOICE_STATUS = {
  PAID: 'paid'
};

// EMMAI
const EMMAI_FORMS = {
  ADD_OR_UPDATE_PRACTITIONER: 'addOrUpdatePractitioner',
  OFFICE_AND_PRACTITIONER_ACTIVITY: 'officeAndPractitionerActivity'
};

const EMMAI_OFFICE_AND_PRACTITIONER_ACTIVITY_FORM_STEPS = {
  FIRST_STEP: 'firstStep',
  SECOND_STEP: 'secondStep'
};

const DATA_INTEGRATION_JOBS_STATUSES = {
  REGISTERED: 'Registered',
  PENDING: 'Pending',
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

const DATA_INTEGRATION_JOBS_ACTIVE_STATUSES = [DATA_INTEGRATION_JOBS_STATUSES.REGISTERED, DATA_INTEGRATION_JOBS_STATUSES.RUNNING, DATA_INTEGRATION_JOBS_STATUSES.PENDING];

const INTEGRATION_JOB_TYPES = {
  REFRESH: 'refresh',
  MASS_IMPORT: 'mass_import'
};

const SUMMARY_MAXIMUM_NUMBER_CHARS = 5000;

const REPORT_PARAMETERS_ACCORDIONS_IDS = {
  PARAMETERS: 'PARAMETERS',
  DATA_SOURCES: 'DATA_SOURCES',
  UPDATES: 'UPDATES'
};

const REPORT_DATASOURCES_AUTHENTICATION_METHODS = {
  ANONYMOUS: {
    id: 'Anonymous',
    label: 'Anonyme',
    formFields: []
  },
  WINDOWS: {
    id: 'Windows',
    label: 'Windows',
    formFields: [
      {id: 'username', label: "Nom d'utilisateur"},
      {id: 'password', label: 'Mot de passe', type: 'password'}
    ]
  },
  BASIC: {
    id: 'Basic',
    label: 'Basic',
    formFields: [
      {id: 'username', label: "Nom d'utilisateur"},
      {id: 'password', label: 'Mot de passe', type: 'password'}
    ]
  },
  OAUTH: {
    id: 'OAuth2',
    label: 'OAuth 2',
    formFields: [{id: 'access_token', label: "Token d'accès", type: 'password'}]
  },
  KEY: {
    id: 'Key',
    label: 'Clé',
    formFields: [{id: 'key', label: "Clé d'accès", type: 'password'}]
  },
  SAS: {
    id: 'Sas',
    label: 'Token',
    formFields: [{id: 'token', label: "Token d'accès", type: 'password'}]
  }
};

const REPORT_DATASOURCES_PRIVACY_LEVELS = {
  NONE: {
    id: 'None',
    label: 'Aucun'
  },
  PUBLIC: {
    id: 'Public',
    label: 'Public'
  },
  ORGANIZATION: {
    id: 'Organizational',
    label: 'Organisationnel'
  },
  PRIVATE: {
    id: 'Private',
    label: 'Privé'
  }
};

const REPORT_UPDATE_HISTORY_STATUSES = {
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  REGISTERED: 'Registered',
  CANCELLED: 'Cancelled'
};

const REPORT_UPDATES_JOBS_STATUSES = {
  REGISTERED: 'Registered',
  NOT_STARTED: 'NotStarted',
  UNKNOWN: 'Unknown',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

const REPORT_UPDATES_JOBS_ACTIVE_STATUSES = [REPORT_UPDATES_JOBS_STATUSES.UNKNOWN, REPORT_UPDATES_JOBS_STATUSES.REGISTERED, REPORT_UPDATES_JOBS_STATUSES.NOT_STARTED];

const WINDOWS_TIMEZONES = [
  {value: 'Dateline Standard Time', label: '(UTC−12:00) Ligne de changement de date ouest'},
  {value: 'Aleutian Standard Time', label: '(UTC−10:00) Îles Aléoutiennes'},
  {value: 'Hawaiian Standard Time', label: '(UTC−10:00) Hawaï'},
  {value: 'Marquesas Standard Time', label: '(UTC−09:30) Îles Marquises'},
  {value: 'Alaskan Standard Time', label: '(UTC−09:00) Alaska'},
  {value: 'Pacific Standard Time', label: '(UTC−08:00) Côte pacifique (États-Unis et Canada)'},
  {value: 'Mountain Standard Time', label: '(UTC−07:00) Rocheuses (États-Unis et Canada)'},
  {value: 'Central Standard Time', label: '(UTC−06:00) Centre (États-Unis et Canada)'},
  {value: 'SA Pacific Standard Time', label: '(UTC−05:00) Bogotá, Lima, Quito'},
  {value: 'Eastern Standard Time', label: '(UTC−05:00) Est (États-Unis et Canada)'},
  {value: 'Atlantic Standard Time', label: '(UTC−04:00) Atlantique (Canada)'},
  {value: 'Newfoundland Standard Time', label: '(UTC−03:30) Terre-Neuve'},
  {value: 'Argentina Standard Time', label: '(UTC−03:00) Buenos Aires'},

  {value: 'UTC-02', label: '(UTC−02:00) Temps universel coordonné -2'},
  {value: 'Azores Standard Time', label: '(UTC−01:00) Açores'},

  {value: 'UTC', label: '(UTC+00:00) Temps universel coordonné'},
  {value: 'GMT Standard Time', label: '(UTC+00:00) Londres, Dublin, Lisbonne'},

  {value: 'W. Europe Standard Time', label: '(UTC+01:00) Berlin, Rome, Stockholm'},
  {value: 'Romance Standard Time', label: '(UTC+01:00) Paris, Madrid, Bruxelles'},

  {value: 'South Africa Standard Time', label: '(UTC+02:00) Harare, Pretoria'},
  {value: 'Israel Standard Time', label: '(UTC+02:00) Jérusalem'},

  {value: 'Turkey Standard Time', label: '(UTC+03:00) Istanbul'},
  {value: 'Russian Standard Time', label: '(UTC+03:00) Moscou'},
  {value: 'Iran Standard Time', label: '(UTC+03:30) Téhéran'},

  {value: 'Arabian Standard Time', label: '(UTC+04:00) Abou Dabi, Mascate'},
  {value: 'Pakistan Standard Time', label: '(UTC+05:00) Islamabad, Karachi'},
  {value: 'India Standard Time', label: '(UTC+05:30) Inde'},
  {value: 'Nepal Standard Time', label: '(UTC+05:45) Katmandou'},

  {value: 'Bangladesh Standard Time', label: '(UTC+06:00) Dacca'},
  {value: 'Myanmar Standard Time', label: '(UTC+06:30) Rangoun'},
  {value: 'SE Asia Standard Time', label: '(UTC+07:00) Bangkok, Jakarta'},

  {value: 'China Standard Time', label: '(UTC+08:00) Pékin, Hong Kong'},
  {value: 'Tokyo Standard Time', label: '(UTC+09:00) Tokyo'},
  {value: 'Korea Standard Time', label: '(UTC+09:00) Séoul'},

  {value: 'AUS Eastern Standard Time', label: '(UTC+10:00) Sydney, Melbourne'},
  {value: 'New Zealand Standard Time', label: '(UTC+12:00) Auckland, Wellington'},
  {value: 'Tonga Standard Time', label: '(UTC+13:00) Tonga'},
  {value: 'Line Islands Standard Time', label: '(UTC+14:00) Îles Line'}
];

export {
  API_ENDPOINTS,
  CONNECTORS,
  MONTHS,
  POWER_BI_REPORTS,
  POWER_BI_PAGES,
  POWER_BI_REPORT_FILTERS,
  POWER_BI_SLICERS,
  POWER_BI_REPORT_BOOKMARKS,
  POWER_BI_VISUALS,
  DEFAULT_PROJECTS_SCHEMAS,
  STRIPE_PORTAL_URLS,
  PRODUCTS_BILLING_SCHEMES,
  PRODUCTS_BILLING_INTERVALS,
  BASE_URL,
  REPORT_LABELS,
  REPORT_URL_FORMATTED_IDS,
  SNACKBAR_ACTIONS,
  BREAKPOINTS,
  REGULAR_EXPRESSIONS,
  REGULAR_EXPRESSIONS_ERROR_TEXTS,
  VAT_RATES,
  PROJECTS_VIEW_TYPES,
  LOGO_THEME_VARIANTS,
  DEFAULT_WORKSPACE,
  EMBED_REPORT_DEFAULT_SETTINGS,
  EMBED_EDITABLE_REPORT_SETTINGS,
  CHATBOT_SIDEBAR_WIDTH,
  CONVERSATIONS_TIME_PERIODS_TRANSLATIONS,
  VISUALS_TYPES_USER_CAN_SHARE_WITH_COPILOT,
  CHATBOT_USER_MESSAGE_TYPES,
  CHATBOT_MESSAGES_ROLES,
  CHATBOT_ASSISTANT_ANSWER_STATE,
  MAXIMUM_NUMBER_OF_CHABOT_MESSAGES_IN_TRIAL,
  MAXIMUM_NUMBER_OF_CHABOT_THREADS,
  TIIME_AUTHORIZE_URL,
  TIIME_CLIENT_ID,
  TIIME_OAUTH_CALLBACK_REDIRECT_URI,
  PENNYLANE_AUTHORIZE_URL,
  PENNYLANE_OAUTH_CALLBACK_REDIRECT_URI,
  FULLL_AUTHORIZE_URL,
  FULLL_OAUTH_CALLBACK_REDIRECT_URI,
  SPREADSHEET_TARGETS,
  SPINNER_TYPES,
  EMPLOYEE_TYPES,
  APP_ROUTES,
  MAXIMUM_NUMBER_OF_WORKSPACES,
  MAXIMUM_NUMBER_OF_CUSTOM_REPORTS,
  MAXIMUM_TRANSCRIPT_AUDIO_FILE_SIZE,
  TRANSCRIPT_AUDIO_FILE_SIZE_WARNING_MESSAGE,
  REPORT_LOADING_STEPS,
  USERS_AUTHORIZATION_LEVELS,
  POWER_BI_PROJECT_SLICER_NAME,
  PROJECTS_GROUPING_TYPES,
  FORM_FIELD_TYPES,
  SORT_TYPES,
  CONNECTORS_ON_CLICK_ACTIONS,
  MODULES_SIDEBAR_WIDTH,
  REPORT_MODULES_CATEGORIES,
  REPORT_MODULES_IDS,
  NON_EXPORTABLE_VISUAL_TYPES,
  REPORT_EXPORTING_STEPS,
  POWER_BI_VISUALS_SELECTORS_NEEDED_FOR_EXPORT,
  EXPORTED_REPORT_FILE_TYPES,
  REPORT_EXPORT_FEATURES,
  FONT_FAMILIES,
  DEFAULT_FILE_FORMAT_ACCEPTED,
  SPREADSHEETS_IDS,
  ADD_USER_FORMS_IDS,
  SIDEBAR_SINGLE_CARD_HEIGHT,
  SIDEBAR_SINGLE_CARD_SECURITY_THRESHOLD,
  SIDEBAR_CONTACT_BUTTON_SIZE,
  SIDEBAR_MORE_ICON_SIZE,
  CONNECTOR_LOGIN_PROCESS_TYPE_LOCALSTORAGE_KEY,
  USER_ROLES,
  ROUNDING_MODES,
  EXPORT_FORMATS,
  DRIVN_CONTACT_EMAIL,
  INVOICE_STATUS,
  appBaseUrl,
  tiimeOauthCallbackRoute,
  pennylaneOauthCallbackRoute,
  fulllOauthCallbackRoute,
  EMMAI_FORMS,
  EMMAI_OFFICE_AND_PRACTITIONER_ACTIVITY_FORM_STEPS,
  DATA_INTEGRATION_JOBS_STATUSES,
  INTEGRATION_JOB_TYPES,
  DATA_INTEGRATION_JOBS_ACTIVE_STATUSES,
  SUMMARY_MAXIMUM_NUMBER_CHARS,
  REPORT_PARAMETERS_ACCORDIONS_IDS,
  REPORT_DATASOURCES_AUTHENTICATION_METHODS,
  REPORT_DATASOURCES_PRIVACY_LEVELS,
  REPORT_UPDATE_HISTORY_STATUSES,
  REPORT_UPDATES_JOBS_STATUSES,
  REPORT_UPDATES_JOBS_ACTIVE_STATUSES,
  WINDOWS_TIMEZONES
};
